import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  cn,
  Input,
  Label,
  Modal,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Textarea,
  toast,
  useDebouncedValue,
} from "@devhop/ui";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
  ChevronRightIcon,
  Loader2Icon,
  MapPinIcon,
  MinusIcon,
  PackageSearchIcon,
  PlusIcon,
  SearchIcon,
  ShoppingCartIcon,
  SparklesIcon,
  Trash2Icon,
  UserIcon,
  UserPlusIcon,
  XIcon,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type { MemberTierDTO } from "@/modules/customers/domain/contracts";
import type { CustomerDTO } from "@/modules/customers/presentation/api/client";
import type {
  StockProductGroupDTO,
  VariantWithStockDTO,
} from "@/modules/inventory/domain/contracts";
import { getStockStatus } from "@/modules/inventory/domain/contracts";
import {
  inventoryApi,
  type StockFilterProductOption,
} from "@/modules/inventory/presentation/api/client";
import type { CustomerAddressDTO } from "@/modules/pos/presentation/api/client";
import {
  usePosAllCustomerGroupsQuery,
  usePosAllMemberTiersQuery,
  usePosCheckoutMutation,
  usePosCustomerAddressesQuery,
  usePosCustomerSearchQuery,
  usePosDistrictsQuery,
  usePosHalBranchesQuery,
  usePosHalDistrictsQuery,
  usePosHalProvincesQuery,
  usePosProvincesQuery,
  usePosQuickCreateCustomerMutation,
  usePosStockInfiniteQuery,
  useRecordSocialTransferPaymentMutation,
} from "@/modules/pos/presentation/api/queries";
import { useCarriersQuery } from "@/modules/shipping/presentation/api/queries";
import { useSocialSalesSourcesActiveForPosQuery } from "@/modules/social-sales-sources/presentation/api/queries";
import type { FilterConditionDTO } from "@/shared/contracts/base";
import { formatPriceLak } from "@/shared/lib/format-price";
import { AppImage } from "@/shared/ui/AppImage";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { ImageKeyUploadField } from "@/shared/ui/ImageKeyUploadField";

type CartLine = {
  variantId: string;
  title: string;
  detail: string;
  unitPrice: number;
  maxQty: number;
  qty: number;
};

function variantDetail(row: VariantWithStockDTO): string {
  const opts =
    row.optionValuesDetails
      ?.map((o) => o.valueName?.lo || o.valueName?.en || "")
      .filter(Boolean)
      .join(", ") ?? "";
  const parts = [row.sku, opts ? `(${opts})` : null];
  return parts.filter(Boolean).join(" • ");
}

function i18nLo(n?: { lo?: string; en?: string; th?: string } | null): string {
  return n?.lo || n?.en || n?.th || "—";
}

function compatibleVariants(
  variants: VariantWithStockDTO[],
  picks: Record<string, string | undefined>,
): VariantWithStockDTO[] {
  return variants.filter((v) => {
    const ov = v.optionValues ?? {};
    for (const [optId, val] of Object.entries(picks)) {
      if (!val) continue;
      if (ov[optId] !== val) return false;
    }
    return true;
  });
}

function allowedValueIdsForOption(
  optionId: string,
  variants: VariantWithStockDTO[],
  picks: Record<string, string | undefined>,
): Set<string> {
  const partial = { ...picks };
  delete partial[optionId];
  const compat = compatibleVariants(variants, partial);
  const set = new Set<string>();
  for (const v of compat) {
    const vid = v.optionValues?.[optionId];
    if (vid) set.add(vid);
  }
  return set;
}

const HEX_COLOR = /^#[0-9a-fA-F]{3,8}$/;

function posOptionValueSelectable(
  optionId: string,
  valueId: string,
  variants: VariantWithStockDTO[],
  picks: Record<string, string | undefined>,
): boolean {
  const partial = { ...picks };
  delete partial[optionId];
  const compat = compatibleVariants(variants, partial);
  return compat.some(
    (v) => v.optionValues?.[optionId] === valueId && v.quantity > 0,
  );
}

function PosPdpStyleOptionRow({
  option,
  variants,
  picks,
  onSelectValue,
}: {
  option: StockFilterProductOption;
  variants: VariantWithStockDTO[];
  picks: Record<string, string>;
  onSelectValue: (optionId: string, valueId: string) => void;
}) {
  const selectedValueId = picks[option.id];
  const allowed = allowedValueIdsForOption(option.id, variants, picks);

  const allValuesLookLikeColor = option.values.every((v) =>
    HEX_COLOR.test(i18nLo(v.value).trim()),
  );
  const useSwatch = allValuesLookLikeColor;

  const selectedLabel = option.values.find((v) => v.id === selectedValueId);

  return (
    <div>
      <p className="mb-2 font-medium text-foreground text-sm">
        {i18nLo(option.name)}
        {selectedLabel ? (
          <>
            {": "}
            {i18nLo(selectedLabel.value)}
          </>
        ) : null}
      </p>
      <div className="flex flex-wrap gap-2">
        {option.values.map((val) => {
          const compatible = allowed.has(val.id);
          const selectable =
            compatible &&
            posOptionValueSelectable(option.id, val.id, variants, picks);
          const selected = selectedValueId === val.id;
          const label = i18nLo(val.value);
          const raw = label.trim();

          if (useSwatch) {
            const isHex = HEX_COLOR.test(raw);
            return (
              <button
                key={val.id}
                type="button"
                aria-label={label}
                title={label}
                onClick={() => selectable && onSelectValue(option.id, val.id)}
                disabled={!selectable}
                className={cn(
                  "size-8 shrink-0 rounded-full border-2 transition-colors",
                  selected
                    ? "border-foreground ring-2 ring-foreground/20"
                    : "border-border hover:border-foreground/50",
                  !selectable && "cursor-not-allowed opacity-40",
                )}
                style={{
                  backgroundColor: isHex ? raw : "var(--color-muted)",
                }}
              />
            );
          }

          return (
            <button
              key={val.id}
              type="button"
              onClick={() => selectable && onSelectValue(option.id, val.id)}
              disabled={!selectable}
              className={cn(
                "min-w-12 rounded border px-3 py-2 font-medium text-sm transition-colors",
                selected
                  ? "border-foreground bg-foreground text-background"
                  : selectable
                    ? "border-border hover:border-foreground"
                    : "cursor-not-allowed border-border text-muted-foreground line-through opacity-60",
              )}
            >
              {label}
            </button>
          );
        })}
      </div>
      {option.values.length > 0 ? (
        allowed.size === 0 ? (
          <p className="mt-1.5 text-muted-foreground text-xs">
            ບໍ່ມີຄ່າທີ່ກົງກັບຕົວເລືອກອື່ນ
          </p>
        ) : [...allowed].every(
            (id) => !posOptionValueSelectable(option.id, id, variants, picks),
          ) ? (
          <p className="mt-1.5 text-muted-foreground text-xs">
            ບໍ່ມີຄ່າທີ່ມີສະຕ໋ອກກົງກັບຕົວເລືອກອື່ນ
          </p>
        ) : null
      ) : null}
    </div>
  );
}

const STOCK_BADGE: Record<
  ReturnType<typeof getStockStatus>,
  { label: string; variant: "destructive" | "warning" | "secondary" }
> = {
  out_of_stock: { label: "ໝົດສະຕ໋ອກ", variant: "destructive" },
  low_stock: { label: "ສະຕ໋ອກຕ່ຳ", variant: "warning" },
  ok: { label: "ພ້ອມຂາຍ", variant: "secondary" },
};

const POS_SKELETON_ROW_KEYS = ["a", "b", "c", "d", "e", "f", "g", "h"] as const;

function PosProductSkeletonRows({ count = 6 }: { count?: number }) {
  const n = Math.min(Math.max(count, 1), POS_SKELETON_ROW_KEYS.length);
  const keys = POS_SKELETON_ROW_KEYS.slice(0, n);

  return (
    <div className="space-y-3">
      {keys.map((key) => (
        <div
          key={`pos-sk-${key}`}
          className="flex items-center gap-4 rounded-xl border border-border/60 bg-card/50 p-4"
        >
          <Skeleton className="size-14 shrink-0 rounded-xl" />
          <div className="min-w-0 flex-1 space-y-2">
            <Skeleton className="h-4 w-4/5 max-w-md" />
            <Skeleton className="h-3 w-3/5 max-w-sm" />
          </div>
          <Skeleton className="hidden h-9 w-24 shrink-0 sm:block sm:w-28" />
        </div>
      ))}
    </div>
  );
}

function PosProductCatalogRow({
  group,
  onSelect,
}: {
  group: StockProductGroupDTO;
  onSelect: () => void;
}) {
  const { product, variants } = group;
  const nameLo = product?.name?.lo || product?.name?.en || "—";
  const initials = nameLo.replace(/\s+/g, "").slice(0, 2).toUpperCase() || "PR";
  const thumb = product?.thumbnailFileKey ?? null;

  const prices = variants.map((v) => v.price);
  const priceLabel =
    prices.length === 0
      ? "—"
      : prices.length === 1
        ? `LAK ${formatPriceLak(prices[0])}`
        : `LAK ${formatPriceLak(Math.min(...prices))} – ${formatPriceLak(Math.max(...prices))}`;

  const sellable = variants.some((v) => v.quantity > 0);
  const variantCount = variants.length;

  return (
    <button
      type="button"
      className={cn(
        "group flex w-full gap-3 rounded-xl border border-border/80 bg-card p-4 text-left shadow-sm transition-all duration-200",
        "hover:border-primary/25 hover:shadow-md",
        (!sellable || variantCount === 0) && "opacity-70",
      )}
      disabled={variantCount === 0 || !sellable}
      onClick={onSelect}
    >
      <div
        className={cn(
          "flex size-14 shrink-0 overflow-hidden rounded-xl border bg-muted/80 font-semibold text-muted-foreground text-xs tracking-tight",
          thumb
            ? "border-border/80 bg-muted/40"
            : "items-center justify-center",
        )}
      >
        {thumb ? (
          <AppImage
            src={thumb}
            alt={nameLo}
            className="size-full"
            fallback={
              <span className="flex size-full items-center justify-center text-[10px]">
                {initials}
              </span>
            }
          />
        ) : (
          initials
        )}
      </div>
      <div className="min-w-0 flex-1 space-y-0.5">
        <p className="truncate font-semibold text-base leading-tight">
          {nameLo}
        </p>
        <p className="text-muted-foreground text-sm">
          {variantCount} ຊຸດໃນຄັງ · {priceLabel}
        </p>
      </div>
      <ChevronRightIcon
        className="size-5 shrink-0 self-center text-muted-foreground group-hover:text-foreground"
        aria-hidden
      />
    </button>
  );
}

function PosVariantPickerRow({
  row,
  onAdd,
}: {
  row: VariantWithStockDTO;
  onAdd: (row: VariantWithStockDTO) => void;
}) {
  const stockStatus = getStockStatus(row.quantity, row.lowStockThreshold);
  const badge = STOCK_BADGE[stockStatus];
  const nameLo = row.product?.name?.lo || row.product?.name?.en || "—";
  const initials = nameLo.replace(/\s+/g, "").slice(0, 2).toUpperCase() || "PR";
  const thumb = row.product?.thumbnailFileKey ?? null;

  return (
    <div
      className={cn(
        "group flex flex-col gap-3 rounded-xl border border-border/70 bg-card p-3 shadow-sm transition-all duration-200 sm:flex-row sm:items-center sm:justify-between sm:gap-4",
        "hover:border-primary/25 hover:shadow-md",
        row.quantity <= 0 && "opacity-70",
      )}
    >
      <div className="flex min-w-0 flex-1 gap-3">
        <div
          className={cn(
            "flex size-14 shrink-0 overflow-hidden rounded-xl border font-semibold text-muted-foreground text-xs tracking-tight",
            thumb
              ? "border-border/80 bg-muted/40"
              : cn(
                  "items-center justify-center bg-muted/80",
                  stockStatus === "ok" &&
                    "border-emerald-500/15 bg-emerald-500/5 text-emerald-900 dark:text-emerald-100",
                  stockStatus === "low_stock" &&
                    "border-amber-500/20 bg-amber-500/5 text-amber-950 dark:text-amber-100",
                ),
          )}
        >
          {thumb ? (
            <AppImage
              src={thumb}
              alt={nameLo}
              className="size-full"
              fallback={
                <span className="flex size-full items-center justify-center text-[10px]">
                  {initials}
                </span>
              }
            />
          ) : (
            initials
          )}
        </div>
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="truncate font-semibold text-base leading-tight">
              {nameLo}
            </p>
            <Badge variant={badge.variant} className="shrink-0 font-normal">
              {badge.label}
            </Badge>
          </div>
          <p className="truncate font-mono text-muted-foreground text-xs">
            {variantDetail(row)}
          </p>
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 pt-0.5">
            <span className="font-semibold text-lg text-primary tabular-nums">
              LAK {formatPriceLak(row.price)}
            </span>
            <span className="text-muted-foreground text-sm tabular-nums">
              ຄັງ{" "}
              <span className="font-medium text-foreground">
                {row.quantity}
              </span>
            </span>
          </div>
        </div>
      </div>
      <Button
        type="button"
        size="default"
        variant="secondary"
        disabled={row.quantity <= 0}
        className="shrink-0 shadow-none transition-transform active:scale-[0.98] sm:min-w-28"
        onClick={() => onAdd(row)}
      >
        <PlusIcon className="size-4" aria-hidden />
        ເພີ່ມໃສ່ກະຕ່າ
      </Button>
    </div>
  );
}

function PosProductOptionModalBody({
  group,
  onAddLine,
}: {
  group: StockProductGroupDTO;
  onAddLine: (v: VariantWithStockDTO) => void;
}) {
  const variants = group.variants;

  const optsQuery = useQuery({
    queryKey: ["inventory", "stock", "pos-product-options", group.productId],
    queryFn: () => inventoryApi.stock.listFilterProductOptions(group.productId),
  });

  const [picks, setPicks] = useState<Record<string, string>>({});

  const activeOptions = useMemo(() => {
    const schema = optsQuery.data ?? [];
    if (!variants.length) return [];
    return [...schema]
      .filter((o) =>
        o.values.some((val) =>
          allowedValueIdsForOption(o.id, variants, {}).has(val.id),
        ),
      )
      .sort((a, b) => a.slug.localeCompare(b.slug));
  }, [optsQuery.data, variants]);

  const matchedVariant = useMemo(() => {
    if (activeOptions.length === 0) return undefined;
    if (!activeOptions.every((o) => picks[o.id])) return undefined;
    return variants.find((v) =>
      activeOptions.every((o) => v.optionValues?.[o.id] === picks[o.id]),
    );
  }, [activeOptions, picks, variants]);

  if (optsQuery.isPending) {
    return (
      <div className="space-y-3 py-2">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (optsQuery.isError) {
    return (
      <div className="space-y-3">
        <p className="text-destructive text-sm">
          ໂຫຼດ OPTION ບໍ່ສຳເລັດ — ລອງອີກຄັ້ງ ຫຼື ເລືອກຈາກລາຍການດ້ານລຸ່ມ
        </p>
        {variants.map((v) => (
          <PosVariantPickerRow key={v.variantId} row={v} onAdd={onAddLine} />
        ))}
      </div>
    );
  }

  if (activeOptions.length === 0) {
    if (variants.length === 1) {
      const v = variants[0];
      const stockStatus = getStockStatus(v.quantity, v.lowStockThreshold);
      const badge = STOCK_BADGE[stockStatus];
      return (
        <div className="space-y-3 rounded-lg border bg-muted/25 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="font-semibold text-lg text-primary tabular-nums">
              LAK {formatPriceLak(v.price)}
            </span>
            <Badge variant={badge.variant}>{badge.label}</Badge>
          </div>
          <p className="font-mono text-muted-foreground text-xs">{v.sku}</p>
          <Button
            type="button"
            className="w-full"
            disabled={v.quantity <= 0}
            onClick={() => onAddLine(v)}
          >
            <PlusIcon className="size-4" aria-hidden />
            ເພີ່ມໃສ່ກະຕ່າ
          </Button>
        </div>
      );
    }

    return (
      <div className="space-y-3">
        <p className="text-muted-foreground text-sm">
          ບໍ່ມີ OPTION ຈາກຄັງສິນຄ້າເປັນຊ່ອງເລືອກ — ເລືອກຕາມລາຍການດ້ານລຸ່ມ
        </p>
        {variants.map((row) => (
          <PosVariantPickerRow
            key={row.variantId}
            row={row}
            onAdd={onAddLine}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="space-y-5">
        {activeOptions.map((opt) => (
          <PosPdpStyleOptionRow
            key={opt.id}
            option={opt}
            variants={variants}
            picks={picks}
            onSelectValue={(optionId, valueId) => {
              setPicks((prev) => ({ ...prev, [optionId]: valueId }));
            }}
          />
        ))}
      </div>

      {matchedVariant ? (
        <div className="space-y-2 rounded-lg border bg-muted/25 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="font-semibold text-lg text-primary tabular-nums">
              LAK {formatPriceLak(matchedVariant.price)}
            </span>
            <Badge
              variant={
                STOCK_BADGE[
                  getStockStatus(
                    matchedVariant.quantity,
                    matchedVariant.lowStockThreshold,
                  )
                ].variant
              }
            >
              {
                STOCK_BADGE[
                  getStockStatus(
                    matchedVariant.quantity,
                    matchedVariant.lowStockThreshold,
                  )
                ].label
              }
            </Badge>
          </div>
          <p className="font-mono text-muted-foreground text-xs">
            SKU {matchedVariant.sku} · ຄັງ {matchedVariant.quantity}
          </p>
          <Button
            type="button"
            className="w-full"
            size="lg"
            disabled={matchedVariant.quantity <= 0}
            onClick={() => onAddLine(matchedVariant)}
          >
            <PlusIcon className="size-4" aria-hidden />
            ເພີ່ມໃສ່ກະຕ່າ
          </Button>
        </div>
      ) : (
        <p className="text-muted-foreground text-sm">
          ເລືອກຄ່າທຸກ OPTION ທີ່ສະແດງເພື່ອເບິ່ງລາຄາແລະສະຕ໋ອກ
        </p>
      )}
    </div>
  );
}

function SelectCustomerModal({
  open,
  onClose,
  onSelect,
}: {
  open: boolean;
  onClose: () => void;
  onSelect: (c: CustomerDTO) => void;
}) {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 400);
  const searchQuery = usePosCustomerSearchQuery(debouncedSearch, open);

  useEffect(() => {
    if (!open) setSearch("");
  }, [open]);

  return (
    <Modal open={open} onOpenChange={(o) => !o && onClose()}>
      <div className="flex max-h-[min(560px,75vh)] flex-col gap-3">
        <div>
          <h3 className="font-semibold text-lg leading-snug">ເລືອກລູກຄ້າ</h3>
          <p className="text-muted-foreground text-sm">ຄົ້ນຫາດ້ວຍຊື່, ອີເມວ, ຫຼືເບີໂທ</p>
        </div>
        <div className="relative">
          <SearchIcon
            className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            autoFocus
            placeholder="ຄົ້ນຫາ..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">
          {searchQuery.isPending && search.trim() ? (
            <div className="space-y-2 py-2">
              {["a", "b", "c"].map((k) => (
                <Skeleton key={k} className="h-16 w-full rounded-xl" />
              ))}
            </div>
          ) : (searchQuery.data?.data ?? []).length === 0 && search.trim() ? (
            <p className="py-8 text-center text-muted-foreground text-sm">
              ບໍ່ພົບລູກຄ້າ
            </p>
          ) : !search.trim() ? (
            <p className="py-8 text-center text-muted-foreground text-sm">
              ພິມຊື່, ອີເມວ, ຫຼືເບີໂທເພື່ອຄົ້ນຫາ
            </p>
          ) : (
            <ul className="space-y-2">
              {(searchQuery.data?.data ?? []).map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    className="w-full rounded-xl border border-border/70 bg-card p-3 text-left transition-colors hover:border-primary/40 hover:bg-muted/30"
                    onClick={() => {
                      onSelect(c);
                      onClose();
                    }}
                  >
                    <p className="font-semibold text-sm">{c.name ?? "—"}</p>
                    <p className="text-muted-foreground text-xs">
                      {[c.email, c.phoneNumber].filter(Boolean).join(" · ")}
                    </p>
                    {c.memberTierName && (
                      <Badge
                        variant="outline"
                        className="mt-1 font-normal text-xs"
                      >
                        {c.memberTierName}
                      </Badge>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="flex justify-end border-border border-t pt-2">
          <Button type="button" variant="outline" onClick={onClose}>
            ປິດ
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function CreateCustomerModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (customerId: string, customer: CustomerDTO) => void;
}) {
  const tiersQuery = usePosAllMemberTiersQuery();
  const groupsQuery = usePosAllCustomerGroupsQuery();
  const createMutation = usePosQuickCreateCustomerMutation();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    memberTierId: "",
  });
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);

  useEffect(() => {
    if (!open) {
      setForm({ name: "", email: "", phoneNumber: "", memberTierId: "" });
      setSelectedGroupIds([]);
    }
  }, [open]);

  const toggleGroup = (groupId: string) => {
    setSelectedGroupIds((prev) =>
      prev.includes(groupId)
        ? prev.filter((id) => id !== groupId)
        : [...prev, groupId],
    );
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      toast.error("ກະລຸນາໃສ່ຊື່ລູກຄ້າ");
      return;
    }
    if (!form.email.trim() && !form.phoneNumber.trim()) {
      toast.error("ກະລຸນາໃສ່ອີເມວ ຫຼື ເບີໂທຢ່າງໜ້ອຍໜຶ່ງ");
      return;
    }
    try {
      const result = await createMutation.mutateAsync({
        name: form.name.trim(),
        email: form.email.trim() || null,
        phoneNumber: form.phoneNumber.trim() || null,
        memberTierId: form.memberTierId || null,
        groupIds: selectedGroupIds.length > 0 ? selectedGroupIds : undefined,
      });
      const selectedTier = tiersQuery.data?.data.find(
        (t) => t.id === form.memberTierId,
      );
      toast.success("ສ້າງລູກຄ້າສຳເລັດ");
      onCreated(result.customerId, {
        id: result.customerId,
        userId: result.userId,
        name: form.name.trim(),
        email: form.email.trim() || null,
        phoneNumber: form.phoneNumber.trim() || null,
        image: null,
        memberTierId: form.memberTierId || null,
        memberTierName: selectedTier ? nameFromI18nTier(selectedTier) : null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
      onClose();
    } catch {
      /* toast from mutation */
    }
  };

  const allGroupOptions = groupsQuery.data?.data ?? [];

  return (
    <Modal open={open} onOpenChange={(o) => !o && onClose()}>
      <div className="flex max-h-[min(640px,80vh)] flex-col gap-4">
        <div>
          <h3 className="font-semibold text-lg leading-snug">
            ສ້າງລູກຄ້າໃໝ່ (ດ່ວນ)
          </h3>
          <p className="text-muted-foreground text-sm">ຕ້ອງໃສ່ຊື່ + ອີເມວ ຫຼື ເບີໂທ</p>
        </div>
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto">
          <div className="space-y-1.5">
            <Label htmlFor="cc-name">ຊື່ລູກຄ້າ *</Label>
            <Input
              id="cc-name"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              placeholder="ຊື່ ແລະ ນາມສະກຸນ"
              autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cc-email">ອີເມວ</Label>
            <Input
              id="cc-email"
              type="email"
              value={form.email}
              onChange={(e) =>
                setForm((p) => ({ ...p, email: e.target.value }))
              }
              placeholder="example@email.com"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cc-phone">ເບີໂທ</Label>
            <Input
              id="cc-phone"
              value={form.phoneNumber}
              onChange={(e) =>
                setForm((p) => ({ ...p, phoneNumber: e.target.value }))
              }
              placeholder="020..."
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cc-tier">ລະດັບສະມາຊິກ (ທາງເລືອກ)</Label>
            <Select
              value={form.memberTierId || undefined}
              onValueChange={(v) => setForm((p) => ({ ...p, memberTierId: v }))}
              disabled={tiersQuery.isPending}
            >
              <SelectTrigger id="cc-tier" className="w-full">
                <SelectValue placeholder="ບໍ່ລະບຸ" />
              </SelectTrigger>
              <SelectContent>
                {(tiersQuery.data?.data ?? []).map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {nameFromI18nTier(t)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {allGroupOptions.length > 0 && (
            <div className="space-y-1.5">
              <Label>ກຸ່ມລູກຄ້າ (ທາງເລືອກ)</Label>
              <div className="flex flex-wrap gap-2">
                {allGroupOptions.map((g) => {
                  const n = g.name as {
                    lo?: string;
                    th?: string;
                    en?: string;
                  };
                  const label = n?.lo ?? n?.th ?? n?.en ?? g.id;
                  const selected = selectedGroupIds.includes(g.id);
                  return (
                    <button
                      key={g.id}
                      type="button"
                      onClick={() => toggleGroup(g.id)}
                      className={`rounded-full border px-3 py-1 text-sm transition-colors ${
                        selected
                          ? "border-primary bg-primary text-primary-foreground"
                          : "hover:bg-muted"
                      }`}
                      style={
                        !selected && g.color
                          ? {
                              borderColor: g.color,
                              color: g.color,
                            }
                          : undefined
                      }
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        <div className="flex flex-col-reverse gap-2 border-border border-t pt-3 sm:flex-row sm:justify-end sm:gap-3">
          <Button
            type="button"
            variant="ghost"
            onClick={onClose}
            disabled={createMutation.isPending}
          >
            ຍົກເລີກ
          </Button>
          <Button
            type="button"
            disabled={createMutation.isPending}
            onClick={() => void handleSubmit()}
          >
            {createMutation.isPending ? (
              <>
                <Loader2Icon className="size-4 animate-spin" aria-hidden />
                ກຳລັງສ້າງ...
              </>
            ) : (
              "ສ້າງລູກຄ້າ"
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function nameFromI18nTier(tier: MemberTierDTO): string {
  const n = tier.name as { lo?: string; th?: string; en?: string } | null;
  if (!n) return "—";
  return n.lo ?? n.th ?? n.en ?? "—";
}

export function PosPage() {
  const nav = useNavigate();
  const searchRef = useRef<HTMLInputElement>(null);
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebouncedValue(searchInput, 400);

  const stockFilters = useMemo((): FilterConditionDTO[] | undefined => {
    const q = debouncedSearch.trim();
    if (!q) return undefined;
    return [{ field: "search", op: "contains", value: q }];
  }, [debouncedSearch]);

  const stockInfinite = usePosStockInfiniteQuery(stockFilters);

  const [cart, setCart] = useState<Record<string, CartLine>>({});
  const [note, setNote] = useState("");
  /** ສ່ວນຫຼຸດທ້າຍບິນ (ປ້ອນເປັນກີບ, ບໍ່ເກີນຍອດຍ່ອຍ) */
  const [posDiscountStr, setPosDiscountStr] = useState("");
  const [posSalesChannel, setPosSalesChannel] = useState<"retail" | "social">(
    "retail",
  );
  const [socialPaymentMethod, setSocialPaymentMethod] = useState<
    "cod" | "online"
  >("cod");
  const [socialSalesSourceId, setSocialSalesSourceId] = useState("");
  const [retailPaymentMethod, setRetailPaymentMethod] = useState<
    "cash" | "bank_transfer"
  >("cash");
  const [retailSlipKey, setRetailSlipKey] = useState("");
  const [retailTransferRef, setRetailTransferRef] = useState("");

  // Customer state
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerDTO | null>(
    null,
  );
  const [showSelectCustomer, setShowSelectCustomer] = useState(false);
  const [showCreateCustomer, setShowCreateCustomer] = useState(false);

  // Shipping state
  type ShippingMode = "none" | "saved" | "new";
  const [shippingMode, setShippingMode] = useState<ShippingMode>("none");
  const [selectedAddressId, setSelectedAddressId] = useState<string | null>(
    null,
  );
  const [shippingForm, setShippingForm] = useState({
    recipientName: "",
    phone: "",
    village: "",
    provinceId: "",
    districtId: "",
    shippingCarrierId: "",
    carrierBranch: "",
    halReceiveBranchId: "",
  });
  const [halBranchSearch, setHalBranchSearch] = useState("");

  const [pickProduct, setPickProduct] = useState<StockProductGroupDTO | null>(
    null,
  );

  const checkout = usePosCheckoutMutation();
  const recordSocialTransfer = useRecordSocialTransferPaymentMutation();
  const socialSourcesQuery = useSocialSalesSourcesActiveForPosQuery(
    posSalesChannel === "social",
  );

  const addressesQuery = usePosCustomerAddressesQuery(
    selectedCustomer?.id ?? null,
  );
  const savedAddresses: CustomerAddressDTO[] = addressesQuery.data ?? [];
  const provincesQuery = usePosProvincesQuery();
  const districtsQuery = usePosDistrictsQuery(
    shippingMode === "new" && shippingForm.provinceId
      ? shippingForm.provinceId
      : null,
  );

  const carriersQuery = useCarriersQuery();
  const carrierRows = carriersQuery.data ?? [];
  const carrierOptions = useMemo(
    () =>
      [...carrierRows.filter((c) => c.enabled)].sort(
        (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0),
      ),
    [carrierRows],
  );
  const selectedCarrier = carrierOptions.find(
    (carrier) => carrier.id === shippingForm.shippingCarrierId,
  );
  const isHalCarrier = selectedCarrier?.code === "HAL";
  const halProvincesQuery = usePosHalProvincesQuery(
    shippingMode === "new" && isHalCarrier,
  );
  const halDistrictsQuery = usePosHalDistrictsQuery(
    shippingForm.provinceId || null,
    shippingMode === "new" && isHalCarrier,
  );
  const halBranchesQuery = usePosHalBranchesQuery({
    provinceId: shippingForm.provinceId,
    districtId: shippingForm.districtId,
    search: halBranchSearch,
    enabled: shippingMode === "new" && isHalCarrier,
  });
  const halBranches = halBranchesQuery.data ?? [];
  const selectedHalBranch =
    halBranches.find(
      (branch) => String(branch.id) === shippingForm.halReceiveBranchId,
    ) ?? null;
  const activeProvinceOptions = isHalCarrier
    ? (halProvincesQuery.data ?? [])
    : (provincesQuery.data?.data ?? []).map((p) => ({
        id: p.id,
        name: i18nLo(p.name),
      }));
  const activeDistrictOptions = isHalCarrier
    ? (halDistrictsQuery.data ?? [])
    : (districtsQuery.data?.data ?? []).map((d) => ({
        id: d.id,
        name: i18nLo(d.name),
      }));

  const [pendingSocialOnlinePay, setPendingSocialOnlinePay] = useState<{
    orderId: string;
    orderCode: string;
  } | null>(null);
  const [transferSlipKey, setTransferSlipKey] = useState("");
  const [transferReference, setTransferReference] = useState("");

  useEffect(() => {
    searchRef.current?.focus();
  }, []);

  // Auto-switch shipping mode when customer addresses load
  useEffect(() => {
    if (!selectedCustomer) {
      setShippingMode("none");
      setSelectedAddressId(null);
      return;
    }
    if (addressesQuery.data && addressesQuery.data.length > 0) {
      setShippingMode("saved");
      const def =
        addressesQuery.data.find((a) => a.isDefault) ?? addressesQuery.data[0];
      setSelectedAddressId(def?.id ?? null);
    } else if (addressesQuery.data && addressesQuery.data.length === 0) {
      setShippingMode("new");
    }
  }, [selectedCustomer, addressesQuery.data]);

  useEffect(() => {
    if (posSalesChannel !== "social") {
      setSocialSalesSourceId("");
      return;
    }
    const list = socialSourcesQuery.data ?? [];
    if (
      socialSalesSourceId &&
      !list.some((s) => s.id === socialSalesSourceId)
    ) {
      setSocialSalesSourceId("");
    }
  }, [posSalesChannel, socialSourcesQuery.data, socialSalesSourceId]);

  const addLine = useCallback((row: VariantWithStockDTO) => {
    if (row.quantity <= 0) return;
    const variantId = row.variantId;
    const title = row.product?.name?.lo || row.product?.name?.en || "—";
    const detail = variantDetail(row);
    setCart((prev) => {
      const cur = prev[variantId];
      const maxQty = row.quantity;
      const nextQty = cur ? Math.min(cur.qty + 1, maxQty) : 1;
      return {
        ...prev,
        [variantId]: {
          variantId,
          title,
          detail,
          unitPrice: row.price,
          maxQty,
          qty: nextQty,
        },
      };
    });
  }, []);

  const incrementQty = useCallback((variantId: string) => {
    setCart((prev) => {
      const cur = prev[variantId];
      if (!cur) return prev;
      const nextQty = Math.min(cur.qty + 1, cur.maxQty);
      return { ...prev, [variantId]: { ...cur, qty: nextQty } };
    });
  }, []);

  const decrementQty = useCallback((variantId: string) => {
    setCart((prev) => {
      const cur = prev[variantId];
      if (!cur) return prev;
      if (cur.qty <= 1) {
        const { [variantId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [variantId]: { ...cur, qty: cur.qty - 1 } };
    });
  }, []);

  const removeLine = useCallback((variantId: string) => {
    setCart((prev) => {
      const { [variantId]: _, ...rest } = prev;
      return rest;
    });
  }, []);

  const clearCart = useCallback(() => {
    setCart({});
    setNote("");
    setPosDiscountStr("");
    setSelectedCustomer(null);
    setShippingMode("none");
    setSelectedAddressId(null);
    setShippingForm({
      recipientName: "",
      phone: "",
      village: "",
      provinceId: "",
      districtId: "",
      shippingCarrierId: "",
      carrierBranch: "",
      halReceiveBranchId: "",
    });
    setHalBranchSearch("");
    setRetailPaymentMethod("cash");
    setRetailSlipKey("");
    setRetailTransferRef("");
  }, []);

  const lines = Object.values(cart);
  const lineCount = lines.length;
  const subtotal = lines.reduce((s, l) => s + l.unitPrice * l.qty, 0);
  const billDiscount = useMemo(() => {
    const digits = posDiscountStr.replace(/\D/g, "");
    if (!digits) return 0;
    const n = Number.parseInt(digits, 10);
    if (!Number.isFinite(n) || n < 0) return 0;
    return Math.min(n, subtotal);
  }, [posDiscountStr, subtotal]);
  const payableTotal = Math.max(0, subtotal - billDiscount);

  const socialCheckoutBlocked =
    posSalesChannel === "social" &&
    (socialSourcesQuery.isLoading ||
      !!socialSourcesQuery.error ||
      (socialSourcesQuery.data?.length ?? 0) === 0 ||
      socialSalesSourceId.trim() === "");

  const retailCheckoutBlocked =
    posSalesChannel === "retail" &&
    retailPaymentMethod === "bank_transfer" &&
    !retailSlipKey.trim();

  const handlePay = useCallback(async () => {
    if (lines.length === 0) return;

    const manualShippingFilled =
      shippingMode === "new" && !!shippingForm.recipientName.trim();
    if (
      manualShippingFilled &&
      shippingForm.shippingCarrierId.trim() &&
      !isHalCarrier &&
      !shippingForm.carrierBranch.trim()
    ) {
      toast.error("ກະລຸນາປ້ອນສາຂາເມື່ອເລືອກຜູ້ຂົນສົ່ງ");
      return;
    }
    if (
      manualShippingFilled &&
      isHalCarrier &&
      !shippingForm.halReceiveBranchId
    ) {
      toast.error("ກະລຸນາເລືອກສາຂາ HAL");
      return;
    }

    try {
      // Build shipping payload
      const shippingPayload: {
        customerId?: string | null;
        shippingAddressId?: string | null;
        shippingRecipientName?: string | null;
        shippingPhone?: string | null;
        shippingVillage?: string | null;
        shippingProvinceId?: string | null;
        shippingDistrictId?: string | null;
        shippingCarrierId?: string | null;
        carrierBranch?: string | null;
        halReceiveBranchId?: number | null;
      } = {};
      const autoVillage =
        selectedHalBranch?.villageName?.trim() ||
        shippingForm.village.trim() ||
        selectedHalBranch?.districtName?.trim() ||
        selectedHalBranch?.name.trim() ||
        null;
      const halReceiveBranchId = shippingForm.halReceiveBranchId
        ? Number(shippingForm.halReceiveBranchId)
        : null;

      if (selectedCustomer) {
        shippingPayload.customerId = selectedCustomer.id;
        if (shippingMode === "saved" && selectedAddressId) {
          shippingPayload.shippingAddressId = selectedAddressId;
        } else if (
          shippingMode === "new" &&
          shippingForm.recipientName.trim()
        ) {
          shippingPayload.shippingRecipientName =
            shippingForm.recipientName.trim() || null;
          shippingPayload.shippingPhone = shippingForm.phone.trim() || null;
          shippingPayload.shippingVillage = isHalCarrier
            ? autoVillage
            : shippingForm.village.trim() || null;
          shippingPayload.shippingProvinceId = shippingForm.provinceId || null;
          shippingPayload.shippingDistrictId = shippingForm.districtId || null;
          shippingPayload.shippingCarrierId =
            shippingForm.shippingCarrierId.trim() || null;
          shippingPayload.carrierBranch = shippingForm.carrierBranch.trim()
            ? shippingForm.carrierBranch.trim()
            : null;
          shippingPayload.halReceiveBranchId = isHalCarrier
            ? halReceiveBranchId
            : null;
        }
      } else if (shippingMode === "new" && shippingForm.recipientName.trim()) {
        shippingPayload.shippingRecipientName =
          shippingForm.recipientName.trim() || null;
        shippingPayload.shippingPhone = shippingForm.phone.trim() || null;
        shippingPayload.shippingVillage = isHalCarrier
          ? autoVillage
          : shippingForm.village.trim() || null;
        shippingPayload.shippingProvinceId = shippingForm.provinceId || null;
        shippingPayload.shippingDistrictId = shippingForm.districtId || null;
        shippingPayload.shippingCarrierId =
          shippingForm.shippingCarrierId.trim() || null;
        shippingPayload.carrierBranch = shippingForm.carrierBranch.trim()
          ? shippingForm.carrierBranch.trim()
          : null;
        shippingPayload.halReceiveBranchId = isHalCarrier
          ? halReceiveBranchId
          : null;
      }

      const result = await checkout.mutateAsync({
        items: lines.map((l) => ({ variantId: l.variantId, quantity: l.qty })),
        salesChannel: posSalesChannel,
        paymentMethod:
          posSalesChannel === "social" ? socialPaymentMethod : undefined,
        socialSalesSourceId:
          posSalesChannel === "social" ? socialSalesSourceId.trim() : undefined,
        retailPaymentMethod:
          posSalesChannel === "retail" ? retailPaymentMethod : undefined,
        slipFileKey:
          posSalesChannel === "retail" &&
          retailPaymentMethod === "bank_transfer"
            ? retailSlipKey.trim()
            : undefined,
        transferReference:
          posSalesChannel === "retail" &&
          retailPaymentMethod === "bank_transfer"
            ? retailTransferRef.trim() || undefined
            : undefined,
        note: note.trim() || undefined,
        discountTotal: billDiscount,
        ...shippingPayload,
      });

      setCart({});
      setNote("");
      setRetailPaymentMethod("cash");
      setRetailSlipKey("");
      setRetailTransferRef("");

      if (posSalesChannel === "social" && socialPaymentMethod === "online") {
        setTransferSlipKey("");
        setTransferReference("");
        setPendingSocialOnlinePay({
          orderId: result.orderId,
          orderCode: result.orderCode,
        });
        toast.success("ສ້າງຄຳສັ່ງຊື້ແລ້ວ", {
          description: `${result.orderCode} · ກະລຸນາອັບໂຫຼດສະລິບແລະບັນທຶກການໂອນ`,
        });
        return;
      }

      if (posSalesChannel === "retail") {
        toast.success("ຊຳລະສຳເລັດ", {
          description: result.orderCode,
        });
        return;
      }

      toast.success("ສ້າງຄຳສັ່ງຊື້ແລ້ວ", {
        description: `${result.orderCode} · ລໍຖ້າຊຳລະຕາມ flow ເວັບ`,
      });
      nav({ to: "/app/orders/$id", params: { id: result.orderId } });
    } catch {
      /* ຂໍ້ຜິດພາດ: toast ຈາກ mutation */
    }
  }, [
    checkout,
    lines,
    nav,
    note,
    posSalesChannel,
    socialPaymentMethod,
    socialSalesSourceId,
    retailPaymentMethod,
    retailSlipKey,
    retailTransferRef,
    selectedCustomer,
    shippingMode,
    selectedAddressId,
    shippingForm,
    billDiscount,
    isHalCarrier,
    selectedHalBranch,
  ]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.key !== "Enter") return;
      if (
        lines.length === 0 ||
        checkout.isPending ||
        pendingSocialOnlinePay !== null ||
        recordSocialTransfer.isPending ||
        socialCheckoutBlocked ||
        retailCheckoutBlocked
      ) {
        return;
      }
      e.preventDefault();
      void handlePay();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [
    checkout.isPending,
    handlePay,
    lines.length,
    pendingSocialOnlinePay,
    recordSocialTransfer.isPending,
    socialCheckoutBlocked,
    retailCheckoutBlocked,
  ]);

  const productGroups = stockInfinite.data?.pages.flatMap((p) => p.data) ?? [];
  const hasSearchFilter = debouncedSearch.trim().length > 0;
  const showInitialSkeleton =
    stockInfinite.isLoading && productGroups.length === 0;

  const stockScrollRef = useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    count:
      productGroups.length > 0
        ? stockInfinite.hasNextPage
          ? productGroups.length + 1
          : productGroups.length
        : 0,
    getScrollElement: () => stockScrollRef.current,
    estimateSize: () => 112,
    overscan: 8,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: infinite scroll trigger ອີງ Virtual ທ້າຍ (ແບບ InfiniteCombobox)
  useEffect(() => {
    if (productGroups.length === 0) return;
    const virtualItems = rowVirtualizer.getVirtualItems();
    const lastItem = virtualItems[virtualItems.length - 1];
    if (!lastItem) return;
    if (
      lastItem.index >= productGroups.length - 1 &&
      stockInfinite.hasNextPage &&
      !stockInfinite.isFetchingNextPage
    ) {
      void stockInfinite.fetchNextPage();
    }
  }, [
    stockInfinite.hasNextPage,
    stockInfinite.fetchNextPage,
    stockInfinite.isFetchingNextPage,
    productGroups.length,
    rowVirtualizer.getVirtualItems(),
  ]);

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "POS" },
          ]}
        />
      </Header>
      <Main fluid className="pb-10">
        {/* Hero */}
        <div className="relative mb-8 overflow-hidden rounded-2xl border bg-linear-to-br from-card via-card to-muted/35 px-5 py-6 shadow-sm ring-1 ring-border/60 sm:px-8 sm:py-7">
          <div className="pointer-events-none absolute -top-16 -right-16 size-56 rounded-full bg-primary/6 blur-3xl" />
          <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-4">
              <div className="flex size-14 shrink-0 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-md shadow-primary/20">
                <ShoppingCartIcon className="size-7" aria-hidden />
              </div>
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="font-bold text-2xl tracking-tight sm:text-3xl">
                    POS
                  </h1>
                  <Badge variant="secondary" className="gap-1 font-normal">
                    <SparklesIcon className="size-3.5" aria-hidden />
                    ຂາຍໜ້າຮ້ານ
                  </Badge>
                </div>
                <p className="mt-1 max-w-xl text-muted-foreground text-sm leading-relaxed">
                  ລາຍການສິນຄ້າໂຫຼດຈາກຄັງ — ແຕະສິນຄ້າເພື່ອເລືອກຕົວເລືອກ (variant).
                  ພິມໃນຊ່ອງເພື່ອກອງ. ຊຳລະແບບ COD (ເງິນສົດໜ້າຮ້ານ). ກົດ{" "}
                  <kbd className="rounded border bg-muted px-1.5 py-0.5 font-mono text-[11px]">
                    Ctrl
                  </kbd>
                  {" + "}
                  <kbd className="rounded border bg-muted px-1.5 py-0.5 font-mono text-[11px]">
                    Enter
                  </kbd>{" "}
                  ເພື່ອຊຳລະເມື່ອກະຕ່າມີສິນຄ້າ.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid gap-8 xl:grid-cols-12 xl:items-start">
          {/* Search & results */}
          <Card className="overflow-hidden border-border/80 shadow-sm xl:col-span-7">
            <CardHeader className="border-b bg-muted/25 pb-4">
              <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2 text-xl">
                    <PackageSearchIcon className="size-5 text-muted-foreground" />
                    ຄົ້ນຫາສິນຄ້າ
                  </CardTitle>
                  <p className="mt-1 text-muted-foreground text-sm">
                    ສິນຄ້າຕໍ່ແຖວ — ແຕະເພື່ອເປີດຕົວເລືອກ; ກອງດ້ວຍຊ່ອງຄົ້ນຫາ; ເລື່ອນລົງໂຫຼດເພີ່ມ.
                  </p>
                </div>
                {stockInfinite.isFetching &&
                productGroups.length > 0 &&
                !stockInfinite.isFetchingNextPage ? (
                  <span className="text-muted-foreground text-xs">
                    ກຳລັງອັບເດດ...
                  </span>
                ) : null}
              </div>
              <div className="relative mt-4 max-w-xl">
                <SearchIcon
                  className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
                  aria-hidden
                />
                <Input
                  ref={searchRef}
                  placeholder="ກອງຕາມຊື່ ຫຼື SKU (ທາງເລືອກ)..."
                  value={searchInput}
                  autoComplete="off"
                  className="h-11 border-border/80 bg-background pl-10 text-base shadow-inner"
                  onChange={(e) => setSearchInput(e.target.value)}
                />
              </div>
            </CardHeader>
            <CardContent className="relative p-4 sm:p-5">
              <div
                ref={stockScrollRef}
                className={cn(
                  "max-h-[min(560px,62vh)] overflow-y-auto pr-1 transition-opacity",
                  stockInfinite.isFetching &&
                    !stockInfinite.isFetchingNextPage &&
                    productGroups.length > 0 &&
                    "opacity-75",
                )}
              >
                {showInitialSkeleton ? <PosProductSkeletonRows /> : null}

                {!showInitialSkeleton &&
                productGroups.length === 0 &&
                !stockInfinite.isLoading ? (
                  <div className="flex flex-col items-center justify-center gap-3 py-14 text-center">
                    <div className="flex size-14 items-center justify-center rounded-full bg-muted">
                      <PackageSearchIcon className="size-7 text-muted-foreground" />
                    </div>
                    <p className="font-medium">
                      {hasSearchFilter ? "ບໍ່ພົບສິນຄ້າ" : "ບໍ່ມີລາຍການໃນຄັງ"}
                    </p>
                    <p className="max-w-sm text-muted-foreground text-sm">
                      {hasSearchFilter
                        ? "ລອງປ່ຽນຄຳຄົ້ນ ຫຼື ກວດ SKU ວ່າຖືກຕ້ອງ."
                        : "ຍັງບໍ່ມີສະຕ໋ອກສຳລັບສິນຄ້າໃດໃນລະບົບ."}
                    </p>
                  </div>
                ) : null}

                {!showInitialSkeleton && productGroups.length > 0 ? (
                  <div
                    className="relative w-full"
                    style={{ height: rowVirtualizer.getTotalSize() }}
                  >
                    {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                      const isLoaderRow =
                        virtualRow.index >= productGroups.length;
                      const group = productGroups[virtualRow.index];
                      return (
                        <div
                          key={virtualRow.key}
                          data-index={virtualRow.index}
                          ref={
                            isLoaderRow
                              ? undefined
                              : rowVirtualizer.measureElement
                          }
                          className="absolute top-0 left-0 w-full"
                          style={{
                            transform: `translateY(${virtualRow.start}px)`,
                          }}
                        >
                          {isLoaderRow ? (
                            <div className="flex h-14 items-center justify-center gap-2 text-muted-foreground text-sm">
                              {stockInfinite.hasNextPage ? (
                                <>
                                  <Loader2Icon
                                    className="size-4 animate-spin"
                                    aria-hidden
                                  />
                                  ກຳລັງໂຫຼດເພີ່ມ...
                                </>
                              ) : null}
                            </div>
                          ) : (
                            <div className="pb-3">
                              <PosProductCatalogRow
                                group={group}
                                onSelect={() => setPickProduct(group)}
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            </CardContent>
          </Card>

          {/* Cart */}
          <Card
            className={cn(
              "overflow-hidden border-border/80 shadow-lg ring-1 ring-border/40 xl:col-span-5",
              "xl:sticky xl:top-20 xl:self-start",
            )}
          >
            <CardHeader className="border-b bg-muted/20 pb-4">
              <div className="flex items-center justify-between gap-2">
                <CardTitle className="flex items-center gap-2 text-xl">
                  <ShoppingCartIcon className="size-5 text-muted-foreground" />
                  ກະຕ່າ
                </CardTitle>
                <Badge variant="outline" className="tabular-nums">
                  {lineCount} ລາຍການ
                </Badge>
              </div>
              <p className="text-muted-foreground text-sm">
                ປັບຈຳນວນ ຫຼື ລົບລາຍການກ່ອນຊຳລະ
              </p>
            </CardHeader>
            <CardContent className="space-y-5 p-4 sm:p-5">
              {lines.length === 0 ? (
                <div className="flex flex-col items-center gap-3 py-12 text-center">
                  <div className="rounded-full bg-muted p-4">
                    <ShoppingCartIcon className="size-9 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="font-medium">ກະຕ່າຍັງວ່າງ</p>
                    <p className="mt-1 text-muted-foreground text-sm">
                      ເພີ່ມສິນຄ້າຈາກລາຍການຊ້າຍ (ຫຼືເທິງໃນມືຖື)
                    </p>
                  </div>
                </div>
              ) : (
                <ul className="max-h-[min(260px,38vh)] space-y-3 overflow-y-auto pr-1">
                  {lines.map((line) => (
                    <li
                      key={line.variantId}
                      className="rounded-xl border border-border/70 bg-muted/20 p-3 sm:p-4"
                    >
                      <div className="flex gap-3">
                        <div className="min-w-0 flex-1 space-y-1">
                          <p className="truncate font-semibold leading-snug">
                            {line.title}
                          </p>
                          <p className="truncate font-mono text-muted-foreground text-xs">
                            {line.detail}
                          </p>
                          <p className="pt-1 font-semibold text-base tabular-nums">
                            LAK {formatPriceLak(line.unitPrice * line.qty)}
                            <span className="ml-2 font-normal text-muted-foreground text-sm">
                              @ LAK {formatPriceLak(line.unitPrice)} ×{" "}
                              {line.qty}
                            </span>
                          </p>
                        </div>
                        <div className="flex shrink-0 flex-col items-end gap-2">
                          <div className="flex items-center rounded-lg border bg-background p-0.5 shadow-sm">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="size-9"
                              aria-label="ຫຼຸດຈຳນວນ"
                              onClick={() => decrementQty(line.variantId)}
                            >
                              <MinusIcon className="size-4" />
                            </Button>
                            <span className="min-w-8 text-center font-semibold text-sm tabular-nums">
                              {line.qty}
                            </span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="size-9"
                              disabled={line.qty >= line.maxQty}
                              aria-label="ເພີ່ມຈຳນວນ"
                              onClick={() => incrementQty(line.variantId)}
                            >
                              <PlusIcon className="size-4" />
                            </Button>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-8 gap-1 text-destructive hover:bg-destructive/10 hover:text-destructive"
                            onClick={() => removeLine(line.variantId)}
                          >
                            <Trash2Icon className="size-3.5" />
                            ລົບ
                          </Button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              <div className="space-y-3 rounded-xl bg-muted/40 p-4 ring-1 ring-border/50">
                <div className="flex items-end justify-between gap-2">
                  <span className="text-muted-foreground text-sm">
                    ລວມຊົ່ວຄາວ
                  </span>
                  <span className="font-bold text-2xl tabular-nums tracking-tight">
                    LAK {formatPriceLak(subtotal)}
                  </span>
                </div>
                {lineCount > 0 ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="w-full text-muted-foreground"
                    onClick={clearCart}
                  >
                    ລ້າງກະຕ່າ
                  </Button>
                ) : null}
              </div>

              {/* Customer card */}
              <div className="space-y-2 rounded-xl border border-border/70 bg-card/40 p-4">
                <p className="flex items-center gap-1.5 font-medium text-sm">
                  <UserIcon
                    className="size-3.5 text-muted-foreground"
                    aria-hidden
                  />
                  ລູກຄ້າ
                </p>
                {selectedCustomer ? (
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-semibold text-sm">
                        {selectedCustomer.name ?? "—"}
                      </p>
                      {selectedCustomer.phoneNumber ? (
                        <p className="text-muted-foreground text-xs">
                          {selectedCustomer.phoneNumber}
                        </p>
                      ) : null}
                      {selectedCustomer.email ? (
                        <p className="truncate text-muted-foreground text-xs">
                          {selectedCustomer.email}
                        </p>
                      ) : null}
                      {selectedCustomer.memberTierName ? (
                        <Badge
                          variant="outline"
                          className="mt-1 font-normal text-xs"
                        >
                          {selectedCustomer.memberTierName}
                        </Badge>
                      ) : null}
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="size-7 shrink-0 text-muted-foreground hover:text-foreground"
                      aria-label="ລ້າງລູກຄ້າ"
                      onClick={() => {
                        setSelectedCustomer(null);
                        setShippingMode("none");
                        setSelectedAddressId(null);
                      }}
                    >
                      <XIcon className="size-3.5" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="flex-1 gap-1.5"
                      onClick={() => setShowSelectCustomer(true)}
                    >
                      <SearchIcon className="size-3.5" aria-hidden />
                      ເລືອກລູກຄ້າ
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="flex-1 gap-1.5"
                      onClick={() => setShowCreateCustomer(true)}
                    >
                      <UserPlusIcon className="size-3.5" aria-hidden />
                      ສ້າງໃໝ່
                    </Button>
                  </div>
                )}
              </div>

              {/* Shipping section */}
              <div className="space-y-3 rounded-xl border border-border/70 bg-card/40 p-4">
                <p className="flex items-center gap-1.5 font-medium text-sm">
                  <MapPinIcon
                    className="size-3.5 text-muted-foreground"
                    aria-hidden
                  />
                  ທີ່ຢູ່ຈັດສົ່ງ
                  <span className="font-normal text-muted-foreground text-xs">
                    (ທາງເລືອກ)
                  </span>
                </p>

                {/* Saved addresses (only when customer selected + has addresses) */}
                {selectedCustomer && addressesQuery.isPending ? (
                  <Skeleton className="h-14 w-full rounded-xl" />
                ) : savedAddresses.length > 0 ? (
                  <div className="space-y-1.5">
                    {savedAddresses.map((addr) => (
                      <button
                        key={addr.id}
                        type="button"
                        className={cn(
                          "w-full rounded-lg border p-3 text-left text-sm transition-colors",
                          selectedAddressId === addr.id &&
                            shippingMode === "saved"
                            ? "border-foreground bg-muted/50"
                            : "border-border/70 hover:border-foreground/40",
                        )}
                        onClick={() => {
                          setShippingMode("saved");
                          setSelectedAddressId(addr.id);
                        }}
                      >
                        <p className="font-medium leading-snug">
                          {addr.label ? `${addr.label} · ` : ""}
                          {addr.recipientName}
                        </p>
                        <p className="truncate text-muted-foreground text-xs">
                          {[
                            addr.village.replace(/\s*\n\s*/g, " "),
                            addr.districtName,
                            addr.provinceName,
                          ]
                            .filter(Boolean)
                            .join(", ")}
                        </p>
                        {addr.shippingCarrierName || addr.carrierBranch ? (
                          <p className="truncate text-muted-foreground text-xs">
                            {[addr.shippingCarrierName, addr.carrierBranch]
                              .filter(Boolean)
                              .join(" · ")}
                          </p>
                        ) : null}
                      </button>
                    ))}
                    <button
                      type="button"
                      className={cn(
                        "w-full rounded-lg border p-3 text-left text-sm transition-colors",
                        shippingMode === "new"
                          ? "border-foreground bg-muted/50"
                          : "border-border/70 border-dashed hover:border-foreground/40",
                      )}
                      onClick={() => {
                        setShippingMode("new");
                        setSelectedAddressId(null);
                      }}
                    >
                      <span className="flex items-center gap-1.5 text-muted-foreground">
                        <PlusIcon className="size-3.5" aria-hidden />
                        ກໍານົດທີ່ຢູ່ໃໝ່
                      </span>
                    </button>
                  </div>
                ) : null}

                {/* New address form */}
                {shippingMode === "new" ||
                (!selectedCustomer && shippingMode !== "none") ? (
                  <div className="space-y-3">
                    {savedAddresses.length === 0 && !selectedCustomer ? (
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant={
                            shippingMode === "none" ? "outline" : "default"
                          }
                          size="sm"
                          className="flex-1"
                          onClick={() =>
                            setShippingMode(
                              shippingMode === "none" ? "new" : "none",
                            )
                          }
                        >
                          {shippingMode === "none" ? "+ ຕື່ມທີ່ຢູ່" : "ຍົກເລີກ"}
                        </Button>
                      </div>
                    ) : null}
                    {shippingMode === "new" ? (
                      <>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="space-y-1">
                            <Label
                              htmlFor="pos-ship-name"
                              className="text-muted-foreground text-xs"
                            >
                              ຊື່ຜູ້ຮັບ
                            </Label>
                            <Input
                              id="pos-ship-name"
                              value={shippingForm.recipientName}
                              onChange={(e) =>
                                setShippingForm((p) => ({
                                  ...p,
                                  recipientName: e.target.value,
                                }))
                              }
                              placeholder="ຊື່ຜູ້ຮັບ"
                              className="h-9 text-sm"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label
                              htmlFor="pos-ship-phone"
                              className="text-muted-foreground text-xs"
                            >
                              ເບີໂທ
                            </Label>
                            <Input
                              id="pos-ship-phone"
                              value={shippingForm.phone}
                              onChange={(e) =>
                                setShippingForm((p) => ({
                                  ...p,
                                  phone: e.target.value,
                                }))
                              }
                              placeholder="020..."
                              className="h-9 text-sm"
                            />
                          </div>
                        </div>
                        <div className="space-y-1">
                          <Label
                            htmlFor="pos-ship-carrier"
                            className="text-muted-foreground text-xs"
                          >
                            ຜູ້ຂົນສົ່ງ
                          </Label>
                          <Select
                            value={shippingForm.shippingCarrierId || undefined}
                            onValueChange={(v) => {
                              setHalBranchSearch("");
                              setShippingForm((p) => ({
                                ...p,
                                shippingCarrierId: v,
                                provinceId: "",
                                districtId: "",
                                carrierBranch: "",
                                halReceiveBranchId: "",
                              }));
                            }}
                            disabled={carriersQuery.isPending}
                          >
                            <SelectTrigger
                              id="pos-ship-carrier"
                              className="h-9 w-full text-sm"
                            >
                              <SelectValue placeholder="ເລືອກຜູ້ຂົນສົ່ງ" />
                            </SelectTrigger>
                            <SelectContent>
                              {carrierOptions.map((c) => (
                                <SelectItem key={c.id} value={c.id}>
                                  {i18nLo(
                                    c.name as {
                                      lo?: string;
                                      en?: string;
                                      th?: string;
                                    },
                                  )}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        {!isHalCarrier ? (
                          <div className="space-y-1">
                            <Label
                              htmlFor="pos-ship-village"
                              className="text-muted-foreground text-xs"
                            >
                              ບ້ານ / ທີ່ຢູ່
                            </Label>
                            <Textarea
                              id="pos-ship-village"
                              value={shippingForm.village}
                              onChange={(e) =>
                                setShippingForm((p) => ({
                                  ...p,
                                  village: e.target.value,
                                }))
                              }
                              placeholder="ບ້ານ / ໜ່ວຍ / ຖະໜົນ"
                              rows={3}
                              className="min-h-18 resize-y text-sm"
                            />
                          </div>
                        ) : null}
                        <div className="grid grid-cols-2 gap-2">
                          <div className="space-y-1">
                            <Label
                              htmlFor="pos-ship-province"
                              className="text-muted-foreground text-xs"
                            >
                              ແຂວງ
                            </Label>
                            <Select
                              value={shippingForm.provinceId || undefined}
                              onValueChange={(v) => {
                                setHalBranchSearch("");
                                setShippingForm((p) => ({
                                  ...p,
                                  provinceId: v,
                                  districtId: "",
                                  carrierBranch: "",
                                  halReceiveBranchId: "",
                                }));
                              }}
                              disabled={
                                !shippingForm.shippingCarrierId ||
                                (isHalCarrier
                                  ? halProvincesQuery.isPending
                                  : provincesQuery.isPending)
                              }
                            >
                              <SelectTrigger
                                id="pos-ship-province"
                                className="h-9 w-full text-sm"
                              >
                                <SelectValue placeholder="ເລືອກແຂວງ" />
                              </SelectTrigger>
                              <SelectContent>
                                {activeProvinceOptions.map((p) => (
                                  <SelectItem key={p.id} value={p.id}>
                                    {p.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1">
                            <Label
                              htmlFor="pos-ship-district"
                              className="text-muted-foreground text-xs"
                            >
                              ເມືອງ
                            </Label>
                            <Select
                              value={shippingForm.districtId || undefined}
                              onValueChange={(v) => {
                                setHalBranchSearch("");
                                setShippingForm((p) => ({
                                  ...p,
                                  districtId: v,
                                  carrierBranch: "",
                                  halReceiveBranchId: "",
                                }));
                              }}
                              disabled={
                                !shippingForm.provinceId ||
                                (isHalCarrier
                                  ? halDistrictsQuery.isPending
                                  : districtsQuery.isPending)
                              }
                            >
                              <SelectTrigger
                                id="pos-ship-district"
                                className="h-9 w-full text-sm"
                              >
                                <SelectValue placeholder="ເລືອກເມືອງ" />
                              </SelectTrigger>
                              <SelectContent>
                                {activeDistrictOptions.map((d) => (
                                  <SelectItem key={d.id} value={d.id}>
                                    {d.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        {!isHalCarrier ? (
                          <div className="space-y-1">
                            <Label
                              htmlFor="pos-ship-carrier-branch"
                              className="text-muted-foreground text-xs"
                            >
                              ສາຂາ / ຈຸດຮັບ
                            </Label>
                            <Input
                              id="pos-ship-carrier-branch"
                              value={shippingForm.carrierBranch}
                              onChange={(e) =>
                                setShippingForm((p) => ({
                                  ...p,
                                  carrierBranch: e.target.value,
                                }))
                              }
                              placeholder="ຕົວຢ່າງ ສາຂາສູນກາງ"
                              className="h-9 text-sm"
                              disabled={!shippingForm.shippingCarrierId}
                            />
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <Label
                              htmlFor="pos-hal-branch-search"
                              className="text-muted-foreground text-xs"
                            >
                              ສາຂາ HAL
                            </Label>
                            <Input
                              id="pos-hal-branch-search"
                              value={halBranchSearch}
                              onChange={(e) =>
                                setHalBranchSearch(e.target.value)
                              }
                              placeholder="ຄົ້ນຫາສາຂາ"
                              className="h-9 text-sm"
                              disabled={
                                !shippingForm.provinceId ||
                                !shippingForm.districtId
                              }
                            />
                            <Select
                              value={
                                shippingForm.halReceiveBranchId || undefined
                              }
                              onValueChange={(v) => {
                                const branch = halBranches.find(
                                  (item) => String(item.id) === v,
                                );
                                setShippingForm((p) => ({
                                  ...p,
                                  halReceiveBranchId: v,
                                  carrierBranch: branch
                                    ? [branch.name, branch.code]
                                        .filter(Boolean)
                                        .join(" · ")
                                    : "",
                                  village:
                                    branch?.villageName?.trim() || p.village,
                                }));
                              }}
                              disabled={
                                !shippingForm.provinceId ||
                                !shippingForm.districtId ||
                                halBranchesQuery.isPending
                              }
                            >
                              <SelectTrigger className="h-9 w-full text-sm">
                                <SelectValue placeholder="ເລືອກສາຂາ HAL" />
                              </SelectTrigger>
                              <SelectContent>
                                {halBranches.map((branch) => (
                                  <SelectItem
                                    key={branch.id}
                                    value={String(branch.id)}
                                  >
                                    {[branch.name, branch.code]
                                      .filter(Boolean)
                                      .join(" · ")}
                                    {branch.villageName
                                      ? ` (${branch.villageName})`
                                      : ""}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {halBranchesQuery.error ? (
                              <p className="text-destructive text-xs">
                                ໂຫຼດສາຂາ HAL ບໍ່ສຳເລັດ
                              </p>
                            ) : null}
                            {!halBranchesQuery.isPending &&
                            !halBranchesQuery.error &&
                            shippingForm.provinceId &&
                            shippingForm.districtId &&
                            halBranches.length === 0 ? (
                              <p className="text-muted-foreground text-xs">
                                ບໍ່ພົບສາຂາ HAL
                              </p>
                            ) : null}
                          </div>
                        )}
                      </>
                    ) : null}
                  </div>
                ) : shippingMode === "none" && !selectedCustomer ? (
                  <button
                    type="button"
                    className="w-full rounded-lg border border-border/70 border-dashed p-3 text-left text-muted-foreground text-sm transition-colors hover:border-foreground/40 hover:text-foreground"
                    onClick={() => setShippingMode("new")}
                  >
                    <span className="flex items-center gap-1.5">
                      <PlusIcon className="size-3.5" aria-hidden />
                      ຕື່ມທີ່ຢູ່ (ທາງເລືອກ)
                    </span>
                  </button>
                ) : null}
              </div>

              {lineCount > 0 ? (
                <div className="space-y-3 rounded-xl border border-border/70 bg-card/40 p-4">
                  <p className="font-medium text-sm">ຊ່ອງທາງຂາຍ</p>
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      type="button"
                      variant={
                        posSalesChannel === "retail" ? "default" : "outline"
                      }
                      className="h-auto min-h-11 whitespace-normal py-2 text-center font-medium text-sm leading-snug"
                      onClick={() => {
                        setPosSalesChannel("retail");
                        setRetailPaymentMethod("cash");
                        setRetailSlipKey("");
                        setRetailTransferRef("");
                      }}
                    >
                      ຫນ້າຮ້ານ
                      {/* <span className="mt-0.5 block font-normal text-[11px] opacity-90">
                        ຢືນຢັນ + ຫັກສະຕ໋ອກທັນທີ
                      </span> */}
                    </Button>
                    <Button
                      type="button"
                      variant={
                        posSalesChannel === "social" ? "default" : "outline"
                      }
                      className="h-auto min-h-11 whitespace-normal py-2 text-center font-medium text-sm leading-snug"
                      onClick={() => {
                        setPosSalesChannel("social");
                        setRetailPaymentMethod("cash");
                        setRetailSlipKey("");
                        setRetailTransferRef("");
                      }}
                    >
                      Social Media
                      {/* <span className="mt-0.5 block font-normal text-[11px] opacity-90">
                        ຄ້າງຊຳລະເຫມືອນເວັບ
                      </span> */}
                    </Button>
                  </div>
                  {posSalesChannel === "retail" ? (
                    <div className="space-y-3">
                      <p className="font-medium text-muted-foreground text-sm">
                        ວິທີຊຳລະ
                      </p>
                      <div className="grid grid-cols-2 gap-2">
                        <Button
                          type="button"
                          variant={
                            retailPaymentMethod === "cash"
                              ? "default"
                              : "outline"
                          }
                          className="h-10 font-medium text-sm"
                          onClick={() => {
                            setRetailPaymentMethod("cash");
                            setRetailSlipKey("");
                            setRetailTransferRef("");
                          }}
                        >
                          ເງິນສົດ
                        </Button>
                        <Button
                          type="button"
                          variant={
                            retailPaymentMethod === "bank_transfer"
                              ? "default"
                              : "outline"
                          }
                          className="h-10 font-medium text-sm"
                          onClick={() =>
                            setRetailPaymentMethod("bank_transfer")
                          }
                        >
                          ໂອນເງິນ
                        </Button>
                      </div>
                      {retailPaymentMethod === "bank_transfer" ? (
                        <div className="space-y-3">
                          <div className="space-y-1.5">
                            <Label
                              htmlFor="pos-retail-transfer-ref"
                              className="text-muted-foreground text-sm"
                            >
                              ເລກອ້າງອີງ (ທາງເລືອກ)
                            </Label>
                            <Input
                              id="pos-retail-transfer-ref"
                              value={retailTransferRef}
                              onChange={(e) =>
                                setRetailTransferRef(e.target.value)
                              }
                              placeholder="ເຊັ່ນ: ທ້າຍເລກອ້າງອີງໃນສະລິບ"
                              className="border-border/80 bg-background font-mono text-sm"
                              disabled={checkout.isPending}
                            />
                          </div>
                          <ImageKeyUploadField
                            label="ສະລິບໂອນເງິນ (ບັງຄັບ)"
                            value={retailSlipKey}
                            onChange={setRetailSlipKey}
                            keyPrefix="uploads/pos/payment-slip"
                            disabled={checkout.isPending}
                            aspectRatio="aspect-video"
                          />
                          <p className="text-muted-foreground text-xs leading-relaxed">
                            ຕ້ອງອັບໂຫຼດສະລິບກ່ອນຊຳລະ
                          </p>
                        </div>
                      ) : (
                        <p className="text-muted-foreground text-xs leading-relaxed">
                          ເງິນສົດ: ຢືນຢັນ + ຫັກສະຕ໋ອກທັນທີ
                        </p>
                      )}
                    </div>
                  ) : null}
                  {posSalesChannel === "social" ? (
                    <div className="space-y-1.5">
                      <label
                        htmlFor="pos-social-pay"
                        className="font-medium text-muted-foreground text-sm"
                      >
                        ວິທີຊຳລະ
                      </label>
                      <Select
                        value={socialPaymentMethod}
                        onValueChange={(v) =>
                          setSocialPaymentMethod(v as "cod" | "online")
                        }
                      >
                        <SelectTrigger
                          id="pos-social-pay"
                          className="w-full border-border/80 bg-background"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="cod">ຊຳລະປາຍທາງ</SelectItem>
                          <SelectItem value="online">ອອນໄລນ໌</SelectItem>
                        </SelectContent>
                      </Select>
                      <div className="space-y-1.5 pt-2">
                        <Label
                          htmlFor="pos-social-source"
                          className="font-medium text-muted-foreground text-sm"
                        >
                          ແຫຼ່ງຂາຍ (ແພລດຟອມ)
                        </Label>
                        <Select
                          value={
                            socialSalesSourceId === ""
                              ? undefined
                              : socialSalesSourceId
                          }
                          onValueChange={(v) => setSocialSalesSourceId(v)}
                          disabled={
                            socialSourcesQuery.isLoading ||
                            !!socialSourcesQuery.error
                          }
                        >
                          <SelectTrigger
                            id="pos-social-source"
                            className="w-full border-border/80 bg-background"
                          >
                            <SelectValue placeholder="ເລືອກແຫຼ່ງຂາຍ..." />
                          </SelectTrigger>
                          <SelectContent>
                            {(socialSourcesQuery.data ?? []).map((s) => (
                              <SelectItem key={s.id} value={s.id}>
                                {s.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {socialSourcesQuery.error ? (
                          <p className="text-destructive text-xs">
                            ໂຫຼດລາຍການແຫຼ່ງບໍ່ສຳເລັດ — ກວດສອບການເຊື່ອມຕໍ່ ຫຼື ສິດອ່ານ
                          </p>
                        ) : null}
                        {!socialSourcesQuery.isLoading &&
                        (socialSourcesQuery.data?.length ?? 0) === 0 ? (
                          <p className="text-muted-foreground text-xs">
                            ບໍ່ມີແຫຼ່ງທີ່ເປີດໃຊ້ — ໃຫ້ແອັດມິນເພີ່ມໃນຕັ້ງຄ່າ
                          </p>
                        ) : null}
                      </div>
                      <p className="text-muted-foreground text-xs leading-relaxed">
                        {socialPaymentMethod === "online" ? (
                          <>
                            ອອນໄລນ໌: ຫຼັງກົດບັນທຶກຄຳສັ່ງຊື້ ຕ້ອງອັບໂຫຼດສະລິບແລະບັນທຶກການໂອນ —
                            ຍັງບໍ່ຫັກສະຕ໋ອກຈົນກວ່າຈະຢືນຢັນອໍເດີ້ຫຼັງຊຳລະແລ້ວ
                          </>
                        ) : (
                          <>ປາຍທາງ: ຄ້າງຊຳລະເຫມືອນເວັບ — ຍັງບໍ່ຫັກສະຕ໋ອກຈົນກວ່າຈະຢືນຢັນ</>
                        )}
                      </p>
                    </div>
                  ) : null}
                </div>
              ) : null}

              <div className="space-y-2">
                <label
                  htmlFor="pos-discount"
                  className="font-medium text-muted-foreground text-sm"
                >
                  ສ່ວນຫຼຸດ (LAK · ທາງເລືອກ)
                </label>
                <Input
                  id="pos-discount"
                  inputMode="numeric"
                  value={posDiscountStr}
                  onChange={(e) => setPosDiscountStr(e.target.value)}
                  placeholder="0"
                  className="border-border/80 bg-background font-mono text-sm"
                  disabled={lineCount === 0}
                />
                {billDiscount > 0 ? (
                  <p className="text-muted-foreground text-xs">
                    ຫຼັງຫັກ: LAK {formatPriceLak(payableTotal)}{" "}
                    <span className="opacity-80">
                      (ຍອດຍ່ອຍ {formatPriceLak(subtotal)} − ຫຼຸດ{" "}
                      {formatPriceLak(billDiscount)})
                    </span>
                  </p>
                ) : null}
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="pos-note"
                  className="font-medium text-muted-foreground text-sm"
                >
                  ໝາຍເຫດ (ທາງເລືອກ)
                </label>
                <Textarea
                  id="pos-note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="ເຊັ່ນ: ຊື່ລູກຄ້າຍ່ອຍ / ອ້າງອີງໃບ..."
                  rows={2}
                  className="resize-none border-border/80 bg-background"
                />
              </div>

              <Button
                type="button"
                className="h-12 w-full gap-2 font-semibold text-base shadow-md transition-transform active:scale-[0.99]"
                size="lg"
                disabled={
                  lines.length === 0 ||
                  checkout.isPending ||
                  recordSocialTransfer.isPending ||
                  socialCheckoutBlocked ||
                  retailCheckoutBlocked
                }
                onClick={() => void handlePay()}
              >
                {checkout.isPending ? (
                  <>
                    <Loader2Icon className="size-5 animate-spin" aria-hidden />
                    ກຳລັງບັນທຶກ...
                  </>
                ) : posSalesChannel === "retail" ? (
                  <>
                    {retailPaymentMethod === "cash" ? "ຊຳລະເງິນສົດ" : "ຊຳລະໂອນ"}
                    {lineCount > 0 ? (
                      <span className="tabular-nums opacity-90">
                        · LAK {formatPriceLak(payableTotal)}
                      </span>
                    ) : null}
                  </>
                ) : (
                  <>
                    ບັນທຶກຄຳສັ່ງຊື້
                    {lineCount > 0 ? (
                      <span className="tabular-nums opacity-90">
                        · LAK {formatPriceLak(payableTotal)}
                      </span>
                    ) : null}
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>
      </Main>

      <Modal
        open={pickProduct !== null}
        onOpenChange={(open) => {
          if (!open) setPickProduct(null);
        }}
      >
        <div className="flex max-h-[min(560px,72vh)] flex-col gap-3">
          <div>
            <h3 className="font-semibold text-lg leading-snug">
              {pickProduct?.product?.name?.lo ||
                pickProduct?.product?.name?.en ||
                "—"}
            </h3>
            <p className="text-muted-foreground text-sm">
              ເລືອກຄ່າ OPTION ທີ່ກ່ຽວຂ້ອງ — ຈາກນັ້ນເພີ່ມເຂົ້າກະຕ່າ
            </p>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto pr-1">
            {pickProduct ? (
              <PosProductOptionModalBody
                key={pickProduct.productId}
                group={pickProduct}
                onAddLine={(v) => {
                  addLine(v);
                  setPickProduct(null);
                }}
              />
            ) : null}
          </div>
          <div className="flex justify-end border-border border-t pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setPickProduct(null)}
            >
              ປິດ
            </Button>
          </div>
        </div>
      </Modal>

      <SelectCustomerModal
        open={showSelectCustomer}
        onClose={() => setShowSelectCustomer(false)}
        onSelect={(c) => {
          setSelectedCustomer(c);
          setShippingMode("none");
          setSelectedAddressId(null);
        }}
      />

      <CreateCustomerModal
        open={showCreateCustomer}
        onClose={() => setShowCreateCustomer(false)}
        onCreated={(_, c) => {
          setSelectedCustomer(c);
          setShippingMode("new");
          setSelectedAddressId(null);
        }}
      />

      <Modal open={pendingSocialOnlinePay !== null}>
        <div className="flex max-h-[min(560px,72vh)] flex-col gap-4">
          <div>
            <h3 className="font-semibold text-lg leading-snug">
              ບັນທຶກການໂອນ + ສະລິບ
            </h3>
            <p className="text-muted-foreground text-sm">
              ອໍເດີ້{" "}
              <span className="font-mono">
                {pendingSocialOnlinePay?.orderCode}
              </span>
            </p>
          </div>
          <div className="space-y-2">
            <Label
              htmlFor="pos-transfer-ref"
              className="text-muted-foreground text-sm"
            >
              ເລກອ້າງອີງ / ບັນຊີປາຍທາງ (ທາງເລືອກ)
            </Label>
            <Input
              id="pos-transfer-ref"
              value={transferReference}
              onChange={(e) => setTransferReference(e.target.value)}
              placeholder="ເຊັ່ນ: ທ້າຍເລກອ້າງອີງໃນສະລິບ"
              className="border-border/80 bg-background font-mono text-sm"
              disabled={recordSocialTransfer.isPending}
            />
          </div>
          <ImageKeyUploadField
            label="ສະລິບໂອນເງິນ (ບັງຄັບ)"
            value={transferSlipKey}
            onChange={setTransferSlipKey}
            keyPrefix={
              pendingSocialOnlinePay
                ? `uploads/orders/${pendingSocialOnlinePay.orderId}/payment-slip`
                : "uploads/orders/payment-slip"
            }
            disabled={
              pendingSocialOnlinePay === null || recordSocialTransfer.isPending
            }
            aspectRatio="aspect-video"
          />
          <div className="flex flex-col-reverse gap-2 border-border border-t pt-3 sm:flex-row sm:justify-end sm:gap-3">
            <Button
              type="button"
              variant="ghost"
              className="text-muted-foreground"
              disabled={recordSocialTransfer.isPending}
              onClick={() => {
                const id = pendingSocialOnlinePay?.orderId;
                setPendingSocialOnlinePay(null);
                setTransferSlipKey("");
                setTransferReference("");
                if (id) nav({ to: "/app/orders/$id", params: { id } });
              }}
            >
              ຂ້າມ · ຍັງບໍ່ບັນທຶກສະລິບ
            </Button>
            <Button
              type="button"
              disabled={
                !pendingSocialOnlinePay ||
                !transferSlipKey.trim() ||
                recordSocialTransfer.isPending
              }
              onClick={() => {
                const p = pendingSocialOnlinePay;
                if (!p || !transferSlipKey.trim()) return;
                void (async () => {
                  try {
                    await recordSocialTransfer.mutateAsync({
                      orderId: p.orderId,
                      slipFileKey: transferSlipKey.trim(),
                      transferReference: transferReference.trim() || undefined,
                    });
                    toast.success("ບັນທຶກການໂອນແລ້ວ", {
                      description: p.orderCode,
                    });
                    setPendingSocialOnlinePay(null);
                    setTransferSlipKey("");
                    setTransferReference("");
                    nav({ to: "/app/orders/$id", params: { id: p.orderId } });
                  } catch {
                    /* toast ຈາກ mutation */
                  }
                })();
              }}
            >
              {recordSocialTransfer.isPending ? (
                <>
                  <Loader2Icon className="size-4 animate-spin" aria-hidden />
                  ກຳລັງບັນທຶກ...
                </>
              ) : (
                "ຢືນຢັນການໂອນ"
              )}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
